import React, { useState } from 'react'
import { Button, Element, Form as BulmaForm } from 'react-bulma-components'
import { Translate } from '../../utils/translations'
import { campaignApi } from '../../campaigns/api'
import { campaign } from '../../campaigns'
import {
  KIT_CONFIRMATION_FIELDS,
  RepairKitRequestFormValues,
  buildKitRequestApiPayload,
} from './repair-kit-form-shared'
import { useAcceptSd500KitRequest } from '../../api/sd500-kit-request'

interface RepairKitConfirmationStepProps {
  /** Values saved on the previous repair kit request step. */
  formValues?: RepairKitRequestFormValues
  /** Campaign-specific quantity map from the product-quantity step. */
  quantity?: Record<string, number>
  language: string
  submissionId?: string
  /** SD500 / EMD500E: accept a draft instead of creating a new record. */
  kitRequestDraftFlow?: boolean
  kitRequestId?: string
  onBack: () => void
  onSuccess: (data: unknown) => void
}

/**
 * Wizard step: recap of kit request data, consent checkbox, then API submission.
 */
const RepairKitConfirmationStep: React.FC<RepairKitConfirmationStepProps> = ({
  formValues,
  quantity = {},
  language,
  submissionId,
  kitRequestDraftFlow = false,
  kitRequestId,
  onBack,
  onSuccess,
}) => {
  const [readConfirmation, setReadConfirmation] = useState(false)

  const useCreateKitRequest = campaignApi.useCreateKitRequest
  const createMutation = useCreateKitRequest({
    mutation: {
      onSuccess: data => {
        onSuccess(data)
      },
    },
  })

  const acceptMutation = useAcceptSd500KitRequest({
    mutation: {
      onSuccess: data => {
        onSuccess(data)
      },
    },
  })

  const isPending = kitRequestDraftFlow ? acceptMutation.isPending : createMutation.isPending

  const handleConfirmSubmit = () => {
    if (!formValues || isPending || !readConfirmation || !submissionId) {
      return
    }

    if (kitRequestDraftFlow) {
      if (!kitRequestId) {
        return
      }
      acceptMutation.mutate({
        kitRequestId,
        data: { submissionId },
      })
      return
    }

    const valuesToSubmit: RepairKitRequestFormValues = {
      ...formValues,
      readConfirmation: true,
    }

    createMutation.mutate({
      data: buildKitRequestApiPayload(valuesToSubmit, quantity, language, submissionId),
    })
  }

  if (!formValues) {
    return (
      <Element textAlign="center">
        <Translate value="fields.required" />
      </Element>
    )
  }

  return (
    <div data-testid="kit-request-confirmation-step">
      <Element>
        {campaign.quantityConfirmationFields!.map(({ quantityKey, labelKey }) => (
          <Element
            key={quantityKey}
            mb={3}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.25rem',
              borderBottom: '1px solid #f0f0f0',
              paddingBottom: '0.75rem',
            }}
          >
            <Element style={{ fontWeight: 600, fontSize: '0.875rem' }}>
              <Translate value={labelKey} />
            </Element>
            <Element style={{ fontSize: '0.95rem', color: '#363636' }}>
              {quantity[quantityKey] ?? 0}
            </Element>
          </Element>
        ))}

        {KIT_CONFIRMATION_FIELDS.map(({ labelKey, getValue }) => (
          <Element
            key={labelKey}
            mb={3}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.25rem',
              borderBottom: '1px solid #f0f0f0',
              paddingBottom: '0.75rem',
            }}
          >
            <Element style={{ fontWeight: 600, fontSize: '0.875rem' }}>
              <Translate value={labelKey} />
            </Element>
            <Element style={{ fontSize: '0.95rem', color: '#363636' }}>
              {getValue(formValues)}
            </Element>
          </Element>
        ))}
      </Element>

      <Element mt={4}>
        <BulmaForm.Control>
          <BulmaForm.Checkbox
            checked={readConfirmation}
            onChange={() => setReadConfirmation(checked => !checked)}
            data-testid="checkbox-readConfirmation"
          >
            <Translate value="fields.readConfirmation" />
          </BulmaForm.Checkbox>
        </BulmaForm.Control>
      </Element>

      <Element
        mt={5}
        style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}
      >
        <Button
          color="light"
          onClick={onBack}
          disabled={isPending}
          data-testid="kit-request-confirmation-back"
        >
          <Translate value="repair.backConfirmation" />
        </Button>
        <Button
          color="primary"
          onClick={handleConfirmSubmit}
          disabled={isPending || !readConfirmation || (kitRequestDraftFlow && !kitRequestId)}
          data-testid="kit-request-confirm"
        >
          <Translate value="repair.validateConfirmation" />
        </Button>
      </Element>
    </div>
  )
}

export default RepairKitConfirmationStep
